import type { ImportFileArg } from './AppAction';
import type { BaseTexture, Material, Node} from '@babylonjs/core';
import type { LockContext } from '@easterngraphics/wcf/modules/utils/async';
import type { Entry, FileEntry} from '@zip.js/zip.js';

import { LinesMesh, PBRMaterial, SceneSerializer } from '@babylonjs/core';
import { BlobReader, BlobWriter, Uint8ArrayReader, Uint8ArrayWriter, ZipReader, ZipWriter } from '@zip.js/zip.js';

import { getArrayBuffer } from '../utils/Blob';

import { appInfo } from './AppInfo';
import { _fakeMessage } from './Communication';
import { DirectoryWriter } from './Filesystem/Container/DirectoryWriter';
import { findAllFiles, readBinaryFile, writeFile } from './Filesystem/Helper';
import { remove, list } from './Filesystem/Wrapper';
import { share } from './Share';

import { getApp } from '@egr/xbox/base-app/App';
import { downloadBlob, getContent } from '@egr/xbox/utils/Download';
import { getUUID } from '@egr/xbox/utils/Helper';
import { stripDoubleSlashesFromPath } from '@egr/xbox/utils/Path';
import { rejectValue, timeout } from '@egr/xbox/utils/Promise';
import { getEnvBoolean } from '@egr/xbox/utils/ReactScriptHelper';

import { Lock } from '@easterngraphics/wcf/modules/utils/async';
import { isNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

export function synchronizeWebAction(value: 'background' | 'foreground'): void {
    _fakeMessage({
        transaction_id: -1,
        type: 'webaction',
        action: 'synchronize',
        arguments: value
    });
}

export async function zipDirectory(path: string): Promise<ArrayBuffer> {
    const l: number = path.length;
    const zipWriter = new ZipWriter<Blob>(new BlobWriter('application/zip'));

    for await (const file of findAllFiles(path)) {
        const content: ArrayBuffer = await readBinaryFile(file);
        await zipWriter.add(file.substr(l), new Uint8ArrayReader(new Uint8Array(content)));
    }

    const zipBlob: Blob = await zipWriter.close();
    return zipBlob.arrayBuffer();
}

export async function unzip(content: ArrayBuffer, path: string): Promise<void> {
    const zipReader = new ZipReader(new BlobReader(new Blob([content])));

    const container: DirectoryWriter = new DirectoryWriter(path);
    const lock: Lock = new Lock();

    async function unzipFile(entry: FileEntry) {
        const lockContext: LockContext = await lock.acquireLock();
        try {
            const fileContent: Uint8Array = await entry.getData(new Uint8ArrayWriter());
            const contentBuffer: ArrayBuffer = getArrayBuffer(fileContent);
            await container.addFile(contentBuffer, entry.filename);
        } finally {
            lockContext.unlock();
        }
    };

    try {
        const entries = await zipReader.getEntries();

        const promises: Array<Promise<void>> = entries.map(
            (entry: Entry) => {
                // ignore folders and hidden files like `.DS_Store`
                if (entry.directory || entry.filename.startsWith('.')) {
                    return Promise.resolve();
                }

                return unzipFile(entry);
            },
            []
        );

        await Promise.all(promises);
    } finally {
        await zipReader.close();
    }
}

export async function openInputDialog(callback: (input: HTMLInputElement) => Promise<void>, accept: string = '.zip'): Promise<void> {
    return new Promise((resolve: VoidFunction, reject: (error: Error) => void): void => {
        const attachInput: boolean = getEnvBoolean('E2E_ATTACH_OPEN_FILE_INPUT', false);
        const input: HTMLInputElement = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        input.onchange = async (): Promise<void> => {
            try {
                await callback(input);
                resolve();
            } catch (error) {
                reject(error as Error);
            } finally {
                if (attachInput) {
                    document.body.removeChild(input);
                }
            }
        };

        if (attachInput) {
            input.id = 'e2e-openFile-input';
            document.body.appendChild(input);
        } else {
            input.click();
        }
    });
}

export function openFile(args?: ImportFileArg, delayWebAction?: Promise<void>): Promise<void> {
    const accept: string = (args?.file_types != null) ? args.file_types.join(',') : '.pec,.pbox,.obk,.eox';
    return openInputDialog(
        async (input: HTMLInputElement): Promise<void> => {
            let importPath: string;
            const fileList: FileList | null = input.files;
            if (fileList == null || fileList.length < 1) {
                throw new Error('no files');
            }

            const extension: string = fileList[0].name.split('.').slice(-1)[0].toLocaleLowerCase();
            if (extension === 'pbox') {
                importPath = '/imports/' + getUUID() + '/';
                const promises: Array<Promise<void>> = [];
                const zipReader = new ZipReader(new BlobReader(fileList[0]));
                try {
                    const entries = await zipReader.getEntries();
                    for (const entry of entries) {
                        if (entry.directory) {
                            continue;
                        }

                        const zipPath = entry.filename;
                        promises.push((async (): Promise<void> => {
                            const fileContent: Uint8Array = await entry.getData(new Uint8ArrayWriter());
                            const contentBuffer: ArrayBuffer = getArrayBuffer(fileContent);
                            return writeFile(
                                stripDoubleSlashesFromPath(importPath + zipPath),
                                contentBuffer
                            );
                        })());
                    }
                } finally {
                    await zipReader.close();
                }

                await Promise.all(promises);
            } else {
                importPath = '/imports/' + getUUID() + '.' + extension;
                await writeFile(
                    importPath,
                    fileList[0]
                );
            }

            const fileExtension: string | undefined = fileList[0].name.split('.').pop();
            if (isNullOrEmpty(fileExtension)) {
                throw new Error('empty file extension');
            }

            const triggerWebAction: VoidFunction = (): void => {
                // ToDo: use new app api module
                _fakeMessage({
                    transaction_id: -1,
                    type: 'webaction',
                    action: 'openfile',
                    arguments: {
                        type: fileExtension.toLocaleLowerCase(),
                        path: importPath,
                        context: args != null ? args.context : undefined
                    }
                });
            };

            if (delayWebAction != null) {
                void (async (): Promise<void> => {
                    await delayWebAction;
                    triggerWebAction();
                })();
            } else {
                triggerWebAction();
            }
        },
        accept
    );
}

export async function exportRootFolder(filename: string = 'root'): Promise<void> {
    const exportPath: string = '/tmp/export_root.zip';

    await rejectValue(remove(exportPath), undefined);
    const content: ArrayBuffer = await zipDirectory('/');

    if (appInfo == null) {
        downloadBlob(
            new Blob(
                [new Uint8Array(content)],
                {
                    type: 'application/octet-stream'
                }
            ),
            filename + '.zip'
        );
    } else {
        try {
            await writeFile(exportPath, content);
            await share(
                {
                    path: exportPath,
                    contentType: 'application/zip',
                    filename: filename + '.zip',
                    remove_path_after_share: true
                },
            );
        } catch {
            await rejectValue(remove(exportPath), undefined);
        }
    }
}

async function importZip(url: string, path: string): Promise<void> {
    const content: ArrayBuffer = await getContent(url);
    await unzip(content, path);
}

export function importProject(url: string): Promise<void> {
    return importZip(url, '/projects/' + getUUID() + '/');
}

export async function importRootFolder(url?: string): Promise<void> {
    const rootItems: Array<string> = await list('/');
    if (rootItems.length) {
        throw new Error('Root folder cannot be imported');
    }

    if (isNullOrEmpty(url)) {
        return openInputDialog(async (input: HTMLInputElement) => {
            const file: File | undefined = input.files?.[0];
            if (file != null) {
                await unzip(
                    await file.arrayBuffer(),
                    '/'
                );
            }
        });
    }

    return importZip(url, '/');
}

export async function emulateWebglContextLost(): Promise<void> {
    const app = await getApp();
    const looseContext = app.sceneManager.getViewer().app.engine._gl.getExtension('WEBGL_lose_context');

    looseContext?.loseContext();
    window.setTimeout(() => {
        looseContext?.restoreContext();
    }, 3000);
}

export async function triggerErrorDialog(): Promise<void> {
    void (async () => {
        await timeout(1);
        throw new Error('debug fatal error');
    })();
}

export async function exportBabylonScene(): Promise<void> {
    const app = await getApp();

    // unselect all
    app.coreApp.model.setSelection(null);
    app.coreApp.model.clearSubElementSelection();

    // remove unneeded sceneelements
    const elements = Array.from(app.coreApp.model.elements);
    for (const element of elements) {
        if (element.node.name.toLowerCase().includes('floor') ||
            element.node.name.toLowerCase().includes('room:test') ||
            element.node.name.toLowerCase().includes('shadow') ||
            element.node.name.toLowerCase().includes('crosshair')
        ) {
            element.node.dispose();
        }
    }

    const rootNodes = Array.from(app.coreApp.scene.rootNodes);
    removeNodes(rootNodes);

    // remove unneeded Textures
    const textures = Array.from(app.coreApp.scene.textures);
    removeTextures(textures);

    // reset reflection Env maps
    app.coreApp.model.materials.environmentMode = 'Default';

    const materials = Array.from(app.coreApp.scene.materials);
    removeMaterials(materials);

    // export babylon scene
    const serializedScene = SceneSerializer.Serialize(app.coreApp.scene);
    const strScene = JSON.stringify(serializedScene);
    const blob = new Blob([strScene], { type: "octet/stream" });

    // remove postprocesses
    const postProcesses = Array.from(app.coreApp.scene.postProcesses);
    for (const postprocess of postProcesses) {
        postprocess.dispose();
    }

    downloadBlob(blob, 'scene.babylon');
}

function removeNodes(nodes: Array<Node>) {
    // remove unneeded meshes and nodes

    for (const mesh of nodes) {
        if (mesh.name.toLowerCase().includes('floor') ||
            mesh.name.toLowerCase().includes('shadow') ||
            mesh.name.toLowerCase().includes('wall_crosshair') ||
            mesh.id.toLowerCase().includes('ground')
        ) {
            mesh.dispose();
        }

        if (mesh instanceof LinesMesh) {
            mesh.dispose();
        }
    }
}

function removeTextures(textures: Array<BaseTexture>) {
    for (const texture of textures) {
        if (texture.name.includes('w-cf') ||
            texture.name.includes('data:') ||
            texture.name.includes('defaultHDR') ||
            texture.name.includes('shadow')
        ) {
            texture.dispose();
        }
    }
}

function removeMaterials(materials: Array<Material>) {
    for (const material of materials) {
        if (material instanceof PBRMaterial) {
            material.reflectionTexture = null;
        }

        // remove materials
        if (material.name === 'FloorPlan' ||
            material.name === 'groundMaterial' ||
            material.name === 'ThickLines' ||
            material.name === 'colorShader' ||
            material.name === 'shadowPlane' ||
            material.name === 'shadowPlaneDepth'
        ) {
            material.dispose();
        }
    }
}